import { CSSProperties, FC, useCallback, useMemo, useState } from 'react';
import { captureException } from '@sentry/react';
import { Button, Col, Divider, Dropdown, Row } from 'antd';
import { TbFileSpreadsheet, TbFileTypeCsv, TbTableExport } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { SmallTooltip } from '@/shared/components/tooltips';
import { getPlanHistoricalDataYears } from '@/shared/pricing';
import { useUserStore } from '@/stores';
import { downloadSpreadsheet } from '@/utilities/spreadsheet';

import { isDividendHistoryRowLocked, normalizeDividendHistoryRows } from '../../financialSeries';

interface DividendHistoryTableExportDropdownProps {
    buttonStyle?: CSSProperties;
    company?: unknown;
    data: Array<Record<string, unknown>>;
}

type SpreadsheetFormat = 'csv' | 'xlsx';

const resolveCompanySymbol = (company: unknown): string | undefined => {
    if (!company || typeof company !== 'object') {
        return undefined;
    }

    const record = company as Record<string, unknown>;
    const symbol = typeof record.symbol === 'string' ? record.symbol : record.tickerSymbol;

    return typeof symbol === 'string' && symbol.trim() ? symbol.trim().toUpperCase() : undefined;
};

const DividendHistoryTableExportDropdown: FC<DividendHistoryTableExportDropdownProps> = ({
    buttonStyle,
    company,
    data,
}): JSX.Element => {
    const { token } = useAntDesignTheme();
    const { selectedUser: user } = useUserStore();
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [exportingFormat, setExportingFormat] = useState<SpreadsheetFormat | null>(null);
    const historicalDataYears = getPlanHistoricalDataYears(user?.plan);
    const companySymbol = resolveCompanySymbol(company);
    const exportRows = useMemo(() => {
        const rows = normalizeDividendHistoryRows(data).filter(
            (row) => !isDividendHistoryRowLocked(row, historicalDataYears),
        );

        return [
            [
                'Ex Date',
                'Declaration Date',
                'Record Date',
                'Pay Date',
                'Frequency',
                'Dividend',
                'Adjusted Dividend',
                'Yield',
            ],
            ...rows.map((row) => [
                row.exDividendDate,
                row.declarationDate ?? '',
                row.recordDate ?? '',
                row.paymentDate ?? '',
                row.frequency ?? '',
                row.dividend ?? '',
                row.adjustedDividend ?? '',
                row.yield ?? '',
            ]),
        ];
    }, [data, historicalDataYears]);

    const handleExport = useCallback(
        async (format: SpreadsheetFormat) => {
            if (exportingFormat) {
                return;
            }

            setExportingFormat(format);
            const label = format === 'csv' ? 'CSV' : 'Excel';
            const toastId = toast.loading(`Downloading ${label}`, {
                description: 'Preparing the dividend history export.',
            });

            try {
                const symbolPrefix = companySymbol ? `${companySymbol}-` : '';
                const timestamp = new Date().toISOString().replace(/[:.]/gu, '-');

                await downloadSpreadsheet(exportRows, `${symbolPrefix}Dividends-${timestamp}`, format, 'Dividends');
                toast.success(`${label} Download Started`, {
                    description: 'Your export should appear in your downloads shortly.',
                    id: toastId,
                });
                setDropdownOpen(false);
            } catch (error) {
                captureException(error);
                toast.error(`Error Downloading ${label}`, { id: toastId });
            } finally {
                setExportingFormat(null);
            }
        },
        [companySymbol, exportRows, exportingFormat],
    );

    const exportDropdown = (
        <div
            style={{
                backgroundColor: token.colorBgElevated,
                border: `1px solid ${token.colorBorder}`,
                borderRadius: token.borderRadius,
                boxShadow: token.boxShadowSecondary,
                minWidth: '16rem',
                padding: '0.25rem',
            }}
        >
            <small
                style={{
                    color: token.colorTextTertiary,
                    display: 'block',
                    fontWeight: 500,
                    padding: '0.25rem 0.5rem 0.375rem',
                }}
            >
                Dividend History
            </small>
            <Divider style={{ margin: '0 0 0.25rem' }} />
            <Button
                block
                disabled={Boolean(exportingFormat && exportingFormat !== 'csv')}
                loading={exportingFormat === 'csv'}
                type='text'
                onClick={() => void handleExport('csv')}
            >
                <Row align='middle' gutter={[8, 0]} style={{ width: '100%' }} wrap={false}>
                    <Col flex='none'>
                        <TbFileTypeCsv size='1rem' style={{ verticalAlign: '-0.175rem' }} />
                    </Col>
                    <Col flex='none'>Export to CSV</Col>
                    <Col flex='auto' />
                    <Col flex='none'>
                        <small style={{ color: token.colorTextTertiary, verticalAlign: '1px' }}>.csv</small>
                    </Col>
                </Row>
            </Button>
            <Button
                block
                disabled={Boolean(exportingFormat && exportingFormat !== 'xlsx')}
                loading={exportingFormat === 'xlsx'}
                type='text'
                onClick={() => void handleExport('xlsx')}
            >
                <Row align='middle' gutter={[8, 0]} style={{ width: '100%' }} wrap={false}>
                    <Col flex='none'>
                        <TbFileSpreadsheet size='1rem' style={{ verticalAlign: '-0.175rem' }} />
                    </Col>
                    <Col flex='none'>Export to Excel</Col>
                    <Col flex='auto' />
                    <Col flex='none'>
                        <small style={{ color: token.colorTextTertiary, verticalAlign: '1px' }}>.xlsx</small>
                    </Col>
                </Row>
            </Button>
        </div>
    );

    return (
        <Dropdown
            open={dropdownOpen}
            popupRender={() => exportDropdown}
            overlayStyle={{ zIndex: 1050 }}
            placement='bottomLeft'
            trigger={['click']}
            onOpenChange={setDropdownOpen}
        >
            <span onClick={(event) => event.stopPropagation()} onMouseDown={(event) => event.stopPropagation()}>
                <SmallTooltip placement='topLeft' title='Export Table'>
                    <Button
                        aria-label='Export dividend history'
                        icon={<TbTableExport style={{ color: token.colorTextTertiary, verticalAlign: '-0.1rem' }} />}
                        loading={Boolean(exportingFormat)}
                        size='small'
                        style={{ marginRight: '0.25rem', ...buttonStyle }}
                        type='text'
                    />
                </SmallTooltip>
            </span>
        </Dropdown>
    );
};

export default DividendHistoryTableExportDropdown;
